import { iSignalFormField } from "./signal-form-field";
import { AbstractControl } from '@angular/forms';

type ValidatorFn<T> = (value: T) => string[];

export function composeValidators<T>(
  ...validators: ValidatorFn<T>[]
): ValidatorFn<T> {
  return (value: T) =>
    validators.flatMap((v) => v(value));
}

export function required<T extends string | number | null | undefined>(message = 'This field is required'): ValidatorFn<T> {
  return (value: T) => {

    if (value === null || value === undefined) {
      return [message];
    }

    if (typeof value === 'string') {
      return value.trim() === '' ? [message] : [];
    }

    if (typeof value === 'number') {
      return isNaN(value) ? [message] : [];
    }

    return [];
  };
}

export function numeric<T extends string | number | null | undefined>(
  message = 'Must be numeric'
): ValidatorFn<T> {
  return (value: T) => {

    if (value === null || value === undefined) {
      return [];
    }

    // Handle string
    if (typeof value === 'string') {
      const v = value.trim();
      if (v === '') return []; // let required() handle empty
      return /^\d+$/.test(v) ? [] : [message];
    }

    // Handle number
    if (typeof value === 'number') {
      return Number(value) >= 0
        ? []
        : [message];
    }

    return [];
  };
}

export function postiveNumber<T extends string | number | null | undefined>(
  message = `Number can not be less than zero`
): ValidatorFn<T> {
  return (value) => {

    const v = Number(value)
    if( v == 0) return []
    return (v > 0) ? [] : [message];
  };
}

export function nonZero(
  message = `Number cannot be zero`
): ValidatorFn<string | number> {
  return (value) => {

    const v = typeof value === 'string' ? Number(value) : value

    return (v == 0) ? [message] : [];
  };
}


export function exactLength(
  length: number,
  message = `Must be exactly ${length} characters`
): ValidatorFn<string> {
  return (value) => {
    const v = value?.trim?.() ?? '';
    if (!v) return [];
    return v.length === length ? [] : [message];
  };
}

export function website(
  message = 'Must be a valid website URL'
): ValidatorFn<string> {
  return (value) => {
    const v = value?.trim?.() ?? '';
    if (!v) return [];

    const regex =
      /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;

    return regex.test(v) ? [] : [message];
  };
}

export function email(
  message = 'Must be a valid email address'
): ValidatorFn<string> {
  return (value) => {
    const v = value?.trim?.() ?? '';
    if (!v) return [];
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    return ok ? [] : [message];
  };
}

export function phone(
  message = 'Must be a valid phone number'
): ValidatorFn<string> {
  return (value) => {
    const v = value?.trim?.() ?? '';
    if (!v) return [];

    const digits = v.replace(/\D/g, '');
    if (digits.length !== 10) return [message];

    return [];
  };
}

export function requiredIf<T>(
  message: string,
  ...fields: iSignalFormField<unknown>[]
): ValidatorFn<T> {
  return (value: T) => {
    const shouldRequire = fields.some((field) => {
      const fieldValue = field.value();

      if (fieldValue === null || fieldValue === undefined) return false;
      if (typeof fieldValue === 'string') return fieldValue.trim() !== '';

      return true;
    });

    if (!shouldRequire) return [];

    if (value === null || value === undefined) return [message];

    if (typeof value === 'string' && value.trim() === '') {
      return [message];
    }

    return [];
  };
}

/**
 * Parse anything into a number if possible.
 * Handles: "$1,234.56", " 1 234 ", "10%", "(123.45)", etc.
 * Returns null if not parseable / empty.
 */
function coerceNumber(input: unknown): number | null {
  if (input === null || input === undefined) return null;

  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  if (typeof input !== "string") return null;

  let s = input.trim();
  if (!s) return null;

  // Handle parentheses as negative: "(123.45)" => "-123.45"
  const parenNegative = /^\((.*)\)$/.test(s);
  if (parenNegative) {
    s = "-" + s.replace(/^\(|\)$/g, "");
  }

  // Remove currency symbols, commas, spaces, and most non-numeric chars,
  // but keep digits, dot, and minus.
  // Examples:
  // "$1,234.50" => "1234.50"
  // "10%" => "10"
  s = s.replace(/[^\d.-]/g, "");

  // Guard against strings that are just "-" or "." etc.
  if (!s || s === "-" || s === "." || s === "-.") return null;

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Error if current field's numeric value > other field's numeric value.
 *
 * Usage:
 *  customDiscountedRate: SignalFormField<number | null>(
 *    null,
 *    composeValidators(
 *      numeric(),
 *      notGreaterThanField(() => normalRate, 'Custom rate cannot exceed normal rate')
 *    )
 *  )
 */
export function notGreaterThanField<T>(
  otherField: () => iSignalFormField<AbstractControl>,
  message = "Value cannot be greater than the other field"
): ValidatorFn<T> {
  return (value: T) => {
    const left = coerceNumber(value);
    const right = coerceNumber(otherField()?.value());

    // If either side isn't a valid number, don't raise this comparison error.
    // Let required()/numeric() handle it instead.
    if (left === null || right === null) return [];

    return left > right ? [message] : [];
  };
}

/**
 * Variant: pass the other field directly (no function).
 * This is fine if the other field reference is stable.
 */
export function notGreaterThanFieldRef<T>(
  fieldRef: iSignalFormField<unknown>,
  message = 'Value cannot be greater than the other field'
): ValidatorFn<T> {
  return (value: T) => {
    const left = coerceNumber(value);
    const right = coerceNumber(fieldRef.value());

    if (left === null || right === null) return [];

    return left > right ? [message] : [];
  };
}

export function minLength(
  length: number,
  message = `Must be at least ${length} characters`
): ValidatorFn<string> {
  return (value) => {
    const v = value?.trim?.() ?? '';
    if (!v) return [];
    return v.length >= length ? [] : [message];
  };
}

export function maxLength(
  length: number,
  message = `Must be no more than ${length} characters`
): ValidatorFn<string> {
  return (value) => {
    const v = value?.trim?.() ?? '';
    if (!v) return [];
    return v.length <= length ? [] : [message];
  };
}

export function matchesField<T>(
  otherField: () => iSignalFormField<T>,
  message = 'Values must match'
): ValidatorFn<T> {
  return (value: T) => {
    const otherValue = otherField()?.value();

    // If either is empty, don't validate here
    // let required() handle that
    if (
      value === null || value === undefined ||
      otherValue === null || otherValue === undefined
    ) {
      return [];
    }

    if (typeof value === 'string' && typeof otherValue === 'string') {
      return value === otherValue ? [] : [message];
    }

    return value === otherValue ? [] : [message];
  };
}


export function birthday(
  message = 'Must be a valid past date'
): ValidatorFn<string> {
  return (value) => {
    const v = value?.trim?.() ?? '';
    if (!v) return [];

    // Ensure we have the yyyy-mm-dd format
    if (v.length < 10 || !v.includes('-')) {
      return ['invalid date'];
    }

    const parts = v.split('-');
    // Explicitly parse as base-10 to avoid any octal issues
    const yyyy = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    const dd = parseInt(parts[2], 10);

    // Create date using local time constructor
    // month is 0-indexed, so 12 (December) becomes 11
    const date = new Date(yyyy, mm - 1, dd);

    // VALIDATION CHECK
    // We check if the date object "rolled over" 
    // (e.g., Feb 31st becomes March 3rd)
    const yearMatches = date.getFullYear() === yyyy;
    const monthMatches = date.getMonth() === mm - 1;
    const dayMatches = date.getDate() === dd;

    if (!yearMatches || !monthMatches || !dayMatches) {
      // If 12/31/1999 fails, it's usually because 'dd' or 'mm' 
      // weren't what you thought they were.
      return ['Date does not exist'];
    }

    const now = new Date();
    now.setHours(0, 0, 0, 0); 

    if (date > now) {
      return [message];
    }

    if (yyyy < 1900) {
      return ['Year is too far in the past'];
    }

    return [];
  };
}

export function datetime(
  message = 'Please enter a complete date'
): ValidatorFn<string | null | undefined> {
  return (value) => {
    // If the input is empty, we return no errors here. 
    // This allows the separate "Validators.required" to handle empty states.
    if (!value) return []; 

    // HTML Date inputs use YYYY-MM-DD. 
    // If the user has only typed "01" (Year 1), the value is "0001-mm-dd".
    // We check if the string actually matches the full ISO format.
    const regex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
    
    if (!regex.test(value)) {
      return [message];
    }

    const [y, m, d] = value.split('-').map(Number);

    // Healthcare/Admin Safety: Prevent year 1, year 0, or year 100
    if (y < 1850 || y > 2100) {
      return ['Please enter a realistic year (1850-2100)'];
    }

    // Calendar Integrity Check (Prevents Feb 30, etc.)
    const date = new Date(y, m - 1, d);
    const isValid = date.getFullYear() === y && 
                    date.getMonth() === m - 1 && 
                    date.getDate() === d;

    return isValid ? [] : ['This date does not exist on the calendar'];
  };
}

export function requiredIfNotEmpty<T>(
  otherField: () => iSignalFormField<AbstractControl>,
  message = 'This field is required'
): ValidatorFn<T> {
  return (value: T) => {
    const otherValue = otherField()?.value();
    
    // Check if the other field has a value
    const isOtherFieldActive = 
      otherValue !== null && 
      otherValue !== undefined && 
      otherValue.toString().trim() !== '';

    // If the other field is empty, this field is valid (not required)
    if (!isOtherFieldActive) {
      return [];
    }

    // If the other field HAS a value, this field MUST NOT be empty
    if (value === null || value === undefined) {
      return [message];
    }

    if (typeof value === 'string' && value.trim() === '') {
      return [message];
    }

    return [];
  };
}

export function maxAge(
  maxAge = 26,
  message = `Age cannot be greater than ${maxAge}`,
  enabled: boolean | (() => boolean) = true
): ValidatorFn<string> {
  return (value) => {
    // 1. Check if the validator is currently disabled
    const isEnabled = typeof enabled === 'function' ? enabled() : enabled;
    if (!isEnabled) return [];

    const v = value?.trim?.() ?? '';
    
    // 2. Wait for the full ISO string (yyyy-mm-dd)
    if (!v || v.length < 10 || !v.includes('-')) return [];

    // 3. Parse the yyyy-mm-dd format
    const parts = v.split('-');
    const yyyy = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    const dd = parseInt(parts[2], 10);

    const birthDate = new Date(yyyy, mm - 1, dd);
    const today = new Date();

    // 4. Calculate precise age
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Adjust if birthday hasn't happened yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    // 5. Check against maxAge
    return age <= maxAge ? [] : [message];
  };
}

export function alphaOnly<T extends string | number | null | undefined>(
  message = "Only letters, hyphens, and apostrophes are allowed"
): ValidatorFn<T> {
  return (value: T) => {
    if (value === null || value === undefined) {
      return [];
    }

    // Handle string
    if (typeof value === 'string') {
      const v = value.trim();
      if (v === '') return []; // let required() handle empty strings
      
      // Regex explained:
      // ^ and $ anchor to start/end
      // [A-Za-z'-]+ ensures at least one character and matches letters, apostrophes, or hyphens
      const regex = /^[A-Za-z' -]+$/;
      
      return regex.test(v) ? [] : [message];
    }

    // If it's a number, it naturally fails this specific regex check
    return [message];
  };
}